/**
 * TOSS Web Application - Date Formatting Utilities
 *
 * Centralized date formatting functions used across the application.
 * All formatters use Russian locale (ru-RU).
 */

/**
 * Format date string to display format with time: dd.MM.yyyy HH:mm
 *
 * Per project policy: dates are shown to minute precision everywhere by default.
 * Use `formatCalendarDate` only for fields that are semantically dates without
 * time (birth date, deadlines, license expiry, etc).
 *
 * @example
 * formatDate('2024-01-15T10:30:00Z') // '15.01.2024, 10:30'
 * formatDate(undefined) // '—'
 */
export function formatDate(dateString: string | undefined | null): string {
  if (!dateString) return '—';
  const date = new Date(dateString);
  return date.toLocaleDateString('ru-RU', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

/**
 * Format date string to display format with time: dd.MM.yyyy HH:mm
 *
 * @param dateString - ISO date string, undefined, or null
 * @returns Formatted date+time string or dash if input is falsy
 *
 * @example
 * formatDateTime('2024-01-15T10:30:00Z') // '15.01.2024, 10:30'
 * formatDateTime(undefined) // '—'
 */
export function formatDateTime(dateString: string | undefined | null): string {
  if (!dateString) return '—';
  const date = new Date(dateString);
  return date.toLocaleDateString('ru-RU', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

/**
 * Format date string to HTML date input format: yyyy-MM-dd
 *
 * Uses the browser's local calendar day, not UTC, because `<input type="date">`
 * is zoneless and should reflect the day the user sees. `toISOString` would
 * shift the day in any timezone west of Greenwich.
 *
 * @param dateString - ISO date string or undefined
 * @returns Date string in yyyy-MM-dd format or empty string if input is falsy/invalid
 *
 * @example
 * formatDateForInput('2024-01-15T10:30:00Z') // '2024-01-15'
 * formatDateForInput(undefined) // ''
 */
export function formatDateForInput(dateString?: string): string {
  if (!dateString) return '';
  const date = new Date(dateString);
  if (Number.isNaN(date.getTime())) return '';
  return formatDateObjectForInput(date);
}

/**
 * Format a Date object to HTML date input format: yyyy-MM-dd (local calendar day).
 *
 * @param date - Date object
 * @returns Date string in yyyy-MM-dd format
 *
 * @example
 * formatDateObjectForInput(new Date(2024, 0, 15)) // '2024-01-15'
 */
export function formatDateObjectForInput(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

/**
 * Format a calendar-date field (stored as UTC timestamp but semantically a day).
 * Uses UTC methods so the displayed day matches the stored UTC date regardless
 * of the viewer's timezone. Use for fields like task deadlines where the
 * time-of-day component is not meaningful.
 */
export function formatCalendarDate(dateString: string | undefined | null): string {
  if (!dateString) return '—';
  const date = new Date(dateString);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleDateString('ru-RU', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    timeZone: 'UTC',
  });
}

/**
 * Format a calendar-date field to HTML date input format (yyyy-MM-dd) using UTC.
 */
export function formatCalendarDateForInput(dateString?: string | null): string {
  if (!dateString) return '';
  const date = new Date(dateString);
  if (Number.isNaN(date.getTime())) return '';
  const year = date.getUTCFullYear();
  const month = String(date.getUTCMonth() + 1).padStart(2, '0');
  const day = String(date.getUTCDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

/**
 * Convert a yyyy-MM-dd input value to an ISO UTC-midnight timestamp.
 * Keeps the picked calendar day stable across timezones.
 */
export function calendarDateInputToIso(value: string | undefined | null): string | undefined {
  if (!value) return undefined;
  const parts = value.split('-');
  if (parts.length !== 3) return undefined;
  const year = Number(parts[0]);
  const month = Number(parts[1]) - 1;
  const day = Number(parts[2]);
  if (!Number.isFinite(year) || !Number.isFinite(month) || !Number.isFinite(day)) return undefined;
  return new Date(Date.UTC(year, month, day)).toISOString();
}

const DEFAULT_TIMEZONE_ID = 'Europe/Moscow';

/**
 * Render an ISO timestamp as wall-clock time in the entity's own IANA timezone.
 *
 * Used by list tables (заявки, ПТО, осмотры) so a record from Vladivostok shows
 * its local creation moment regardless of where the observer is. Falls back to
 * `Europe/Moscow` when `timezoneId` is empty.
 *
 * @example
 * formatLocalDateTime('2026-03-24T20:00:00Z', 'Asia/Vladivostok') // '25.03.2026, 06:00'
 */
export function formatLocalDateTime(
  dateString: string | undefined | null,
  timezoneId: string | undefined | null,
): string {
  if (!dateString) return '—';
  const date = new Date(dateString);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleString('ru-RU', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    timeZone: timezoneId || DEFAULT_TIMEZONE_ID,
  });
}

/**
 * Print a WALL CLOCK exactly as it was stored — a local time at the place of service that the server
 * keeps as `timestamp without time zone` (the ПТО snapshots of ADR-032: `createdAtLocal`,
 * `inspectionDateLocal`, `verifiedAtLocal`, the payroll row's `date`).
 *
 * The API writes every DateTime with a `Z`, so such a value ARRIVES looking like a UTC instant. It is
 * not one: converting it to the browser's zone (what `formatDateTime` does) moves it by the viewer's
 * offset, and the cell then shows another day than the column's header tree, which counts the day
 * from the same snapshot (ADR-046). The digits are read straight from the string, so the result is
 * the same in every browser zone. Same text shape as `formatDateTime` / `formatLocalDateTime`.
 *
 * @example
 * formatWallClock('2026-09-08T23:23:00Z')      // '08.09.2026, 23:23' — in Moscow, Dubai or Vladivostok
 * formatWallClock('2026-09-08T23:23:00.0000000') // '08.09.2026, 23:23'
 * formatWallClock(null)                          // '—'
 */
export function formatWallClock(value: string | undefined | null): string {
  if (!value) return '—';
  const match = /^(\d{4})-(\d{2})-(\d{2})[T ](\d{2}):(\d{2})/.exec(value);
  if (match) {
    const [, year, month, day, hour, minute] = match;
    return `${day}.${month}.${year}, ${hour}:${minute}`;
  }
  // Not an ISO string (never produced by the API): read it as an instant, printed in UTC — the
  // zone a wall clock written with a `Z` round-trips through.
  return formatLocalDateTime(value, 'UTC');
}

/**
 * The browser's IANA zone («Europe/Moscow», «Asia/Novosibirsk»), or `undefined` when the runtime
 * cannot name one. The API client sends it on every request as `X-Client-Timezone`: place-less date
 * columns (created at, received at, audit time) are drawn in this zone, and the server reads their
 * filter days and header-tree days in it too, so the tree offers the dates the cells show (ADR-046).
 */
export function getBrowserTimeZone(): string | undefined {
  try {
    const zone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    return zone || undefined;
  } catch {
    return undefined;
  }
}

/**
 * Produce a compact «МСК», «МСК+10» style label for a timezone identifier.
 *
 * Returns an empty string when the timezone matches Europe/Moscow at the given
 * reference date (caller decides whether to render anything). Caller is
 * responsible for surrounding markup; this only emits the label text.
 *
 * @example
 * formatTimezoneLabel('Asia/Vladivostok') // 'МСК+7'
 * formatTimezoneLabel('Europe/Moscow')    // ''
 */
export function formatTimezoneLabel(
  timezoneId: string | undefined | null,
  refDate: Date = new Date(),
): string {
  const tz = timezoneId || DEFAULT_TIMEZONE_ID;
  if (tz === DEFAULT_TIMEZONE_ID) return '';

  const offsetMinutes = getTimezoneOffsetMinutes(tz, refDate);
  const moscowOffsetMinutes = getTimezoneOffsetMinutes(DEFAULT_TIMEZONE_ID, refDate);
  const diffHours = (offsetMinutes - moscowOffsetMinutes) / 60;

  if (diffHours === 0) return 'МСК';
  const sign = diffHours > 0 ? '+' : '−';
  const absHours = Math.abs(diffHours);
  const hoursText = Number.isInteger(absHours) ? String(absHours) : absHours.toFixed(1);
  return `МСК${sign}${hoursText}`;
}

/**
 * A stored moment by the LK list's rule (`ApplicationTable`'s date cell): the wall clock in the
 * entity's own zone (Moscow when it has none), the zone named when it is not Moscow —
 * «02.10.2026, 14:34», «02.10.2026, 21:34 МСК+7». The reader's browser zone never decides what a
 * stored moment reads, so the same moment prints the same on the list, the card and the history.
 *
 * @example
 * formatZonedDateTime('2026-10-02T11:34:00Z', 'Europe/Moscow')    // '02.10.2026, 14:34'
 * formatZonedDateTime('2026-10-02T11:34:00Z', 'Asia/Vladivostok') // '02.10.2026, 21:34 МСК+7'
 */
export function formatZonedDateTime(
  dateString: string | undefined | null,
  timezoneId: string | undefined | null,
): string {
  const moment = formatLocalDateTime(dateString, timezoneId);
  if (moment === '—' || !dateString) return moment;
  const label = formatTimezoneLabel(timezoneId, new Date(dateString));
  return label ? `${moment} ${label}` : moment;
}

/**
 * Offset of an IANA timezone from UTC in minutes, at the given moment.
 * Positive east of UTC, negative west.
 */
function getTimezoneOffsetMinutes(timezoneId: string, refDate: Date): number {
  // 'longOffset' renders as e.g. "GMT+10:00" or "GMT−03:30".
  const formatter = new Intl.DateTimeFormat('en-US', {
    timeZone: timezoneId,
    timeZoneName: 'longOffset',
  });
  const parts = formatter.formatToParts(refDate);
  const tzNamePart = parts.find((p) => p.type === 'timeZoneName')?.value ?? 'GMT';
  const match = tzNamePart.match(/GMT([+-−])(\d{1,2})(?::(\d{2}))?/);
  if (!match) return 0;
  const sign = match[1] === '-' || match[1] === '−' ? -1 : 1;
  const hours = Number(match[2]);
  const minutes = match[3] ? Number(match[3]) : 0;
  return sign * (hours * 60 + minutes);
}

/**
 * Age in words: «только что», «12 мин назад», «3 ч назад», «вчера», «4 дн назад», and an
 * absolute date once a week has passed — beyond that «сколько-то дней назад» stops being
 * something a person can picture, and the actual date is what they would look for.
 *
 * Both notification feeds read their age through this: the customer's popover and page, and the
 * internal panel since 2026-08-26. Two implementations of «4 дн назад» is two places for the
 * cutoffs to drift, on a string a person compares between the two products.
 */
export function formatRelativeAge(value: string): string {
  const then = new Date(value).getTime();
  if (Number.isNaN(then)) return '';

  const seconds = Math.floor((Date.now() - then) / 1000);
  if (seconds < 60) return 'только что';

  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes} мин назад`;

  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours} ч назад`;

  const days = Math.floor(hours / 24);
  if (days === 1) return 'вчера';
  if (days < 7) return `${days} дн назад`;

  return formatDateTime(value);
}
